/** Pure settings contract shared with the browser. No server-only imports. */
export interface CameraDefaults {
  deviceId: string
  resolution: { width: number; height: number }
  frameRate: number
  mirrorLiveView: boolean
  rotation: 0 | 90 | 180 | 270
}
export interface AudioDefaults {
  microphoneDeviceId: string
  outputDeviceId: string
  microphoneGain: number
  enabled: boolean
  counterSoundEnabled: boolean
  shutterSoundEnabled: boolean
}
export interface PhotoLightDefaults {
  enabled: boolean
  device: string
  wsUrl: string
  baseTopic: string
  mode: 'off' | 'always' | 'auto' | 'session'
  onBeforeSeconds: number
  offAfterSeconds: number
}
export const SCREENSAVER_STYLES = ['classic', 'shutter', 'flash', 'kids', 'business', 'party'] as const
export type ScreensaverStyle = typeof SCREENSAVER_STYLES[number]
export interface ScreensaverDefaults {
  enabled: boolean
  /** Look of the attract loop. 'classic' is the video with floating phrases. */
  style: ScreensaverStyle
  timeout: number
  wakeMethod: 'touch' | 'motion'
  videoAsset: string
  phrases: string[]
  maxMessages: number
  intervalSeconds: number
  motionSensitivity: number
  motionCameraDeviceId: string
  motionCameraLabel: string
}
export interface SlideshowDefaults {
  enabled: boolean
  sortOrder: 'newestFirst' | 'oldestFirst' | 'random'
  contentFilter: 'all' | 'separate' | 'layouts'
  interval: number
  showEventName: boolean
  showQrCode: boolean
  autoRefreshInterval: number
  refreshOnNewPhoto: boolean
  blurBackground: boolean
  transitionEffect: 'fade' | 'zoom' | 'slide'
  loopSlideshow: boolean
  backgroundColor: string
}
export interface CaptureDefaults {
  enabledModes: Array<'photo' | 'gif' | 'boomerang' | 'video'>
  photo: { poseCount: number; delayBeforeFirst: number; delayBetweenPoses: number; reviewTime: number; alsoCreateGif: boolean }
  gif: { frameCount: number; frameDelay: number; quality: number }
  boomerang: { duration: number; speed: number }
  video: { enabled: boolean; maxDuration: number }
  recordSessionVideo: boolean
  finishScreenTimeout: number
}
export interface SettingsValues {
  camera: CameraDefaults
  audio: AudioDefaults
  photoLight: PhotoLightDefaults
  screensaver: ScreensaverDefaults
  slideshow: SlideshowDefaults
  captureBehavior: CaptureDefaults
  dataSaver: { enabled: boolean; quality: number; maxDimension: number }
}
export const SETTINGS_SECTIONS = ['camera', 'audio', 'photoLight', 'screensaver', 'slideshow', 'captureBehavior', 'dataSaver'] as const
export type SettingsSection = typeof SETTINGS_SECTIONS[number]
export type SectionOverride<T> = { mode: 'inherit' } | { mode: 'custom'; value: T }
export interface SettingsInheritance {
  version: 1
  sections: { [K in SettingsSection]?: SectionOverride<SettingsValues[K]> }
}
// Legacy events may have absent optional fields. Do not require a full event here.
export interface SettingsEvent {
  camera?: Partial<CameraDefaults> & { audioInputDeviceId?: string }
  audio?: Partial<AudioDefaults>
  capture?: Partial<CaptureDefaults> & {
    zigbee?: Partial<PhotoLightDefaults>
    attractLoop?: Partial<ScreensaverDefaults>
    dataSaver?: Partial<SettingsValues['dataSaver']>
  }
  kiosk?: Partial<SlideshowDefaults>
  settingsInheritance?: SettingsInheritance
}
export const BUILTIN_SETTINGS: SettingsValues = {
  camera: { deviceId: '', resolution: { width: 1920, height: 1080 }, frameRate: 30, mirrorLiveView: true, rotation: 0 },
  audio: { microphoneDeviceId: '', outputDeviceId: '', microphoneGain: 1, enabled: true, counterSoundEnabled: true, shutterSoundEnabled: true },
  photoLight: { enabled: false, device: 'spotlight', wsUrl: 'ws://localhost:8081/api', baseTopic: 'zigbee2mqtt', mode: 'auto', onBeforeSeconds: 3, offAfterSeconds: 2 },
  screensaver: { enabled: false, style: 'classic', timeout: 60, wakeMethod: 'touch', videoAsset: '/video/bg_video.mp4', phrases: ['Step up and take a photo.', 'Photos are free. Come on in.', 'Pick a pose, we handle the rest.', 'Group shots work best close together.', 'Press the screen when you are ready.', 'Two or more people is more fun.', 'Bring a prop from the table.', 'Your photo is ready in a minute.'], maxMessages: 4, intervalSeconds: 2.5, motionSensitivity: 50, motionCameraDeviceId: '', motionCameraLabel: '' },
  slideshow: { enabled: true, sortOrder: 'newestFirst', contentFilter: 'all', interval: 10, showEventName: true, showQrCode: true, autoRefreshInterval: 10, refreshOnNewPhoto: true, blurBackground: true, transitionEffect: 'fade', loopSlideshow: true, backgroundColor: '#000000' },
  captureBehavior: { enabledModes: ['photo'], photo: { poseCount: 3, delayBeforeFirst: 3, delayBetweenPoses: 2, reviewTime: 5, alsoCreateGif: false }, gif: { frameCount: 10, frameDelay: 200, quality: 10 }, boomerang: { duration: 3, speed: 1 }, video: { enabled: false, maxDuration: 15 }, recordSessionVideo: false, finishScreenTimeout: 20 },
  dataSaver: { enabled: false, quality: 0.75, maxDimension: 1600 },
}

// Picks known keys only, replaces arrays, fills missing fields, preserves false/0/''.
function normalize<T>(base: T, input: unknown): T {
  if (Array.isArray(base)) return structuredClone((Array.isArray(input) ? input : base) as T)
  if (base && typeof base === 'object') {
    const source = input && typeof input === 'object' ? input as Record<string, unknown> : {}
    return Object.fromEntries(Object.entries(base).map(([key, value]) => [key, normalize(value, source[key])])) as T
  }
  return (input === undefined ? base : input) as T
}
export function extractSettings(event: SettingsEvent): SettingsValues {
  const audio = { ...event.audio, microphoneDeviceId: event.audio?.microphoneDeviceId ?? event.camera?.audioInputDeviceId ?? '' }
  return {
    camera: normalize(BUILTIN_SETTINGS.camera, event.camera),
    audio: normalize(BUILTIN_SETTINGS.audio, audio),
    photoLight: normalize(BUILTIN_SETTINGS.photoLight, event.capture?.zigbee),
    screensaver: normalize(BUILTIN_SETTINGS.screensaver, event.capture?.attractLoop),
    slideshow: normalize(BUILTIN_SETTINGS.slideshow, event.kiosk),
    captureBehavior: normalize(BUILTIN_SETTINGS.captureBehavior, event.capture),
    dataSaver: normalize(BUILTIN_SETTINGS.dataSaver, event.capture?.dataSaver),
  }
}
export function inheritAll(): SettingsInheritance { return { version: 1, sections: {} } }
export function customInheritance(values: SettingsValues): SettingsInheritance {
  return { version: 1, sections: Object.fromEntries(SETTINGS_SECTIONS.map(key => [key, { mode: 'custom', value: structuredClone(values[key]) }])) as SettingsInheritance['sections'] }
}
export function migrateLegacy<T extends SettingsEvent>(event: T): T {
  if (event.settingsInheritance) {
    if (event.settingsInheritance.version !== 1) throw new Error('Unsupported settings inheritance version')
    return event
  }
  return { ...event, settingsInheritance: customInheritance(extractSettings(event)) }
}
export function isInherited(event: SettingsEvent, key: SettingsSection): boolean {
  return event.settingsInheritance?.version === 1 && event.settingsInheritance.sections[key]?.mode !== 'custom'
}
export function effectiveSettings(event: SettingsEvent, defaults: SettingsValues): SettingsValues {
  const stored = migrateLegacy(event)
  const legacy = extractSettings(stored)
  return Object.fromEntries(SETTINGS_SECTIONS.map(key => {
    const override = stored.settingsInheritance!.sections[key]
    return [key, normalize(BUILTIN_SETTINGS[key], override?.mode === 'custom' ? override.value : defaults[key] ?? legacy[key])]
  })) as unknown as SettingsValues
}
export function applySettings<T extends SettingsEvent>(event: T, values: SettingsValues): T {
  return {
    ...event,
    camera: { ...event.camera, ...structuredClone(values.camera), audioInputDeviceId: values.audio.microphoneDeviceId },
    audio: structuredClone(values.audio),
    capture: { ...event.capture, ...structuredClone(values.captureBehavior), zigbee: structuredClone(values.photoLight), attractLoop: structuredClone(values.screensaver), dataSaver: structuredClone(values.dataSaver) },
    kiosk: { ...event.kiosk, ...structuredClone(values.slideshow) },
  }
}
export function resolveEvent<T extends SettingsEvent>(event: T, defaults: SettingsValues): T {
  return applySettings(event, effectiveSettings(event, defaults))
}
/** Portable snapshots never rebind to the destination account's defaults. */
export function portableEvent<T extends SettingsEvent>(resolved: T): T {
  return { ...resolved, settingsInheritance: customInheritance(extractSettings(resolved)) }
}

// Strict API validation: only known fields, no public access or service credentials.
export function validateSettings(input: unknown): SettingsValues {
  const fail = (path: string): never => { throw new Error(`Invalid setting: ${path}`) }
  function walk(base: unknown, value: unknown, path: string): void {
    if (Array.isArray(base)) {
      if (!Array.isArray(value) || value.length > 100 || value.some(v => typeof v !== 'string' || v.length > 2000)) fail(path)
    } else if (base && typeof base === 'object') {
      if (!value || typeof value !== 'object' || Array.isArray(value)) fail(path)
      const record = value as Record<string, unknown>
      for (const key of Object.keys(record)) if (!Object.prototype.hasOwnProperty.call(base, key)) fail(`${path}.${key}`)
      for (const [key, field] of Object.entries(base)) walk(field, record[key], `${path}.${key}`)
    } else if (typeof value !== typeof base || (typeof value === 'number' && (!Number.isFinite(value) || value < 0 || value > 100000)) || (typeof value === 'string' && value.length > 4000)) fail(path)
  }
  walk(BUILTIN_SETTINGS, input, 'defaults')
  const v = input as SettingsValues
  const between = (n: number, lo: number, hi: number, path: string) => { if (n < lo || n > hi) fail(path) }
  const oneOf = (value: unknown, choices: unknown[], path: string) => { if (!choices.includes(value)) fail(path) }
  oneOf(v.camera.rotation, [0, 90, 180, 270], 'camera.rotation')
  between(v.camera.resolution.width, 1, 16384, 'camera.resolution.width')
  between(v.camera.resolution.height, 1, 16384, 'camera.resolution.height')
  between(v.camera.frameRate, 1, 240, 'camera.frameRate')
  between(v.audio.microphoneGain, 0, 2, 'audio.microphoneGain')
  oneOf(v.photoLight.mode, ['off', 'always', 'auto', 'session'], 'photoLight.mode')
  if (!/^wss?:\/\/[^\s]+$/.test(v.photoLight.wsUrl)) fail('photoLight.wsUrl')
  between(v.photoLight.onBeforeSeconds, 0, 300, 'photoLight.onBeforeSeconds')
  between(v.photoLight.offAfterSeconds, 0, 300, 'photoLight.offAfterSeconds')
  oneOf(v.screensaver.wakeMethod, ['touch', 'motion'], 'screensaver.wakeMethod')
  oneOf(v.screensaver.style, [...SCREENSAVER_STYLES], 'screensaver.style')
  if (v.screensaver.videoAsset && !/^\/(uploads|video|default-assets)\/[a-zA-Z0-9_./% -]+$/.test(v.screensaver.videoAsset)) fail('screensaver.videoAsset')
  if (v.screensaver.videoAsset.includes('..')) fail('screensaver.videoAsset')
  between(v.screensaver.timeout, 5, 3600, 'screensaver.timeout')
  between(v.screensaver.maxMessages, 0, 100, 'screensaver.maxMessages')
  between(v.screensaver.intervalSeconds, 0.1, 300, 'screensaver.intervalSeconds')
  between(v.screensaver.motionSensitivity, 0, 100, 'screensaver.motionSensitivity')
  oneOf(v.slideshow.sortOrder, ['newestFirst', 'oldestFirst', 'random'], 'slideshow.sortOrder')
  oneOf(v.slideshow.contentFilter, ['all', 'separate', 'layouts'], 'slideshow.contentFilter')
  oneOf(v.slideshow.transitionEffect, ['fade', 'zoom', 'slide'], 'slideshow.transitionEffect')
  between(v.slideshow.interval, 1, 3600, 'slideshow.interval')
  between(v.slideshow.autoRefreshInterval, 0, 3600, 'slideshow.autoRefreshInterval')
  if (!/^#[\da-fA-F]{6}$/.test(v.slideshow.backgroundColor)) fail('slideshow.backgroundColor')
  if (!v.captureBehavior.enabledModes.length || v.captureBehavior.enabledModes.some(m => !['photo', 'gif', 'boomerang', 'video'].includes(m))) fail('captureBehavior.enabledModes')
  between(v.captureBehavior.photo.poseCount, 1, 100, 'captureBehavior.photo.poseCount')
  between(v.captureBehavior.gif.frameCount, 1, 1000, 'captureBehavior.gif.frameCount')
  between(v.captureBehavior.gif.quality, 1, 30, 'captureBehavior.gif.quality')
  between(v.captureBehavior.boomerang.speed, 0.1, 10, 'captureBehavior.boomerang.speed')
  between(v.dataSaver.quality, 0.1, 1, 'dataSaver.quality')
  between(v.dataSaver.maxDimension, 1, 16384, 'dataSaver.maxDimension')
  return structuredClone(v)
}
export function validateInheritance(input: unknown): SettingsInheritance {
  if (!input || typeof input !== 'object') throw new Error('Invalid settings inheritance')
  const envelope = input as SettingsInheritance
  if (envelope.version !== 1 || !envelope.sections || typeof envelope.sections !== 'object' || Array.isArray(envelope.sections)) throw new Error('Unsupported settings inheritance')
  if (Object.keys(envelope).some(k => k !== 'version' && k !== 'sections')) throw new Error('Unknown settings inheritance field')
  for (const [key, override] of Object.entries(envelope.sections)) {
    if (!SETTINGS_SECTIONS.includes(key as SettingsSection) || !override || !['inherit', 'custom'].includes(override.mode)) throw new Error('Invalid settings section')
    if (Object.keys(override).some(k => k !== 'mode' && (k !== 'value' || override.mode !== 'custom'))) throw new Error('Invalid settings override')
    if (override.mode === 'custom') validateSettings({ ...BUILTIN_SETTINGS, [key]: override.value })
  }
  return structuredClone(envelope)
}
